import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MapPin, Clock, Users, Target, Lightbulb, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import ExploreMore from "@/components/ExploreMore";
import axios from "axios";

import useMetaDescription from "@/hooks/useMetaDescription";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback data - always available even if database is empty
const DEFAULT_CASE_STUDIES = {
  "it-services-expansion": {
    id: "1",
    title: "TechCorp IT Services - Mumbai Campus Expansion",
    slug: "it-services-expansion",
    industry: "IT & Technology",
    location: "Mumbai, Maharashtra",
    scope: "350 workstations across 3 floors with collaboration zones and meeting rooms",
    challenge: "TechCorp needed to expand their Mumbai campus to accommodate 350 new engineers while maintaining their open, collaborative culture. The existing furniture vendors couldn't meet their 6-week deadline.",
    solution: "We deployed our rapid-installation modular workstation system with integrated cable management. Custom collaboration pods were designed for agile team meetings. Height-adjustable desks were provided for 20% of workstations to give the team flexibility between sitting and standing positions during long working hours.",
    outcome: "Project completed in 5.5 weeks, under budget. Employee satisfaction scores increased by 23% post-move. The collaboration pods have become the most-used spaces in the office.",
    timeline: "6 weeks",
    images: [
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/vbkl67zd_board%20room.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/nd141fbg_Breakout.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/aq3gcgmc_Capturea.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/xvy56tpb_Captureb.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/c0b8v32j_meeting%20zone.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/jaeuaysw_informal%20seatout%202.PNG"
    ],
    workstations: 350
  },
  "banking-hq-renovation": {
    id: "2",
    title: "National Bank HQ - Executive Floor Renovation",
    slug: "banking-hq-renovation",
    industry: "BFSI",
    location: "Delhi NCR",
    scope: "Complete executive floor redesign including 12 private offices and boardroom",
    challenge: "National Bank required a prestigious executive floor that reflected their 50-year legacy while incorporating modern amenities. Security and privacy were paramount, with specific requirements for acoustic isolation and data security.",
    solution: "Premium veneer workstations with integrated biometric locks. Custom boardroom table seating 24 with built-in AV systems. Acoustic panels for complete sound privacy. Secure document storage with fire rating.",
    outcome: "The renovated floor has hosted 3 international investor meetings. Featured in Banking Today magazine. Client reported 40% reduction in external meeting venue costs.",
    timeline: "8 weeks",
    images: [
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/aq3gcgmc_Capturea.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/xvy56tpb_Captureb.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/vbkl67zd_board%20room.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/nd141fbg_Breakout.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/c0b8v32j_meeting%20zone.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/jaeuaysw_informal%20seatout%202.PNG"
    ],
    workstations: 120
  },
  "coworking-space-setup": {
    id: "3",
    title: "WorkHub - Premium Co-working Space Launch",
    slug: "coworking-space-setup",
    industry: "Co-working",
    location: "Bangalore, Karnataka",
    scope: "450-seat co-working facility with hot desks, private offices, and event space",
    challenge: "WorkHub needed furniture that could withstand heavy daily use while maintaining a premium aesthetic. Flexibility was key - spaces needed to transform for events. Budget constraints required creative solutions.",
    solution: "Modular furniture system allowing quick reconfiguration. Heavy-duty yet elegant workstations. Stackable seating for event space. Phone booths for privacy. Custom cafe furniture for the common areas.",
    outcome: "95% occupancy within 3 months of launch. The space has been featured as one of Bangalore's top co-working destinations. Furniture has maintained appearance despite heavy use.",
    timeline: "10 weeks",
    images: [
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/c0b8v32j_meeting%20zone.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/jaeuaysw_informal%20seatout%202.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/8mmt24wa_playzone.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/ruwxkw0r_reception%202.png",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/vbkl67zd_board%20room.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/nd141fbg_Breakout.PNG"
    ],
    workstations: 450
  },
  "bpo-operations-center": {
    id: "4",
    title: "GlobalConnect BPO - 24/7 Operations Center",
    slug: "bpo-operations-center",
    industry: "BPO / Call Centre",
    location: "Hyderabad, Telangana",
    scope: "800 workstations with 24/7 operations capability, training center, and break rooms",
    challenge: "GlobalConnect's new delivery center needed to support round-the-clock operations with three shifts. Acoustic management was critical as voice-based processes required clear audio. Employee comfort for 8-hour shifts directly impacted customer service quality.",
    solution: "High-density workstations with integrated acoustic panels reducing ambient noise by 40%. 24/7-rated task seating with 5-year warranty. Dedicated break rooms with lounge furniture to support shift transitions.",
    outcome: "Zero furniture-related complaints in the first year. Operator turnover reduced by 20% versus industry average.",
    timeline: "12 weeks",
    images: [
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/ruwxkw0r_reception%202.png",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/vbkl67zd_board%20room.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/nd141fbg_Breakout.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/aq3gcgmc_Capturea.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/xvy56tpb_Captureb.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/c0b8v32j_meeting%20zone.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/jaeuaysw_informal%20seatout%202.PNG",
      "https://customer-assets.emergentagent.com/job_9b093370-34b9-40ba-a843-79d03a3bb890/artifacts/8mmt24wa_playzone.PNG"
    ],
    workstations: 800
  },
  "manufacturing-corporate-office": {
    id: "5",
    title: "SteelWorks Industries - Corporate Office",
    slug: "manufacturing-corporate-office",
    industry: "Manufacturing",
    location: "Ahmedabad, Gujarat",
    scope: "200 workstations, training center, product showcase area, and visitor center",
    challenge: "SteelWorks wanted their corporate office to reflect their manufacturing heritage while providing modern corporate amenities. The visitor center needed to showcase their products to international buyers. The training facility would host both employees and dealer partners.",
    solution: "Custom furniture incorporating exposed metal elements and industrial finishes reflecting the company's product line. The visitor center features integrated product displays within the furniture design. Flexible training room accommodates classroom, workshop, and presentation configurations. Executive areas balance industrial aesthetic with corporate comfort.",
    outcome: "The office became a showcase for client visits, with the visitor center contributing to a 30% increase in dealer sign-ups. Featured in Manufacturing Today as an example of aligned corporate identity. Employee feedback highlighted pride in the distinctive workspace. International buyers specifically request facility tours.",
    timeline: "9 weeks",
    images: [
      "https://customer-assets.emergentagent.com/job_c7ca78d1-ab7b-4712-8b24-d64ce9919df7/artifacts/ldlf27ml_Capture%201.PNG",
      "https://customer-assets.emergentagent.com/job_c7ca78d1-ab7b-4712-8b24-d64ce9919df7/artifacts/5v0vhn5o_Capture%202.PNG",
      "https://customer-assets.emergentagent.com/job_c7ca78d1-ab7b-4712-8b24-d64ce9919df7/artifacts/2bg0ema2_Capture%203.PNG",
      "https://customer-assets.emergentagent.com/job_c7ca78d1-ab7b-4712-8b24-d64ce9919df7/artifacts/v1wad5ly_Capture%204.PNG",
      "https://customer-assets.emergentagent.com/job_c7ca78d1-ab7b-4712-8b24-d64ce9919df7/artifacts/rnnqoywx_Capture%205.PNG",
      "https://customer-assets.emergentagent.com/job_c7ca78d1-ab7b-4712-8b24-d64ce9919df7/artifacts/aut2cn8d_Capture%207.PNG",
      "https://customer-assets.emergentagent.com/job_c7ca78d1-ab7b-4712-8b24-d64ce9919df7/artifacts/k6skxrk7_Capture%208.PNG",
      "https://customer-assets.emergentagent.com/job_c7ca78d1-ab7b-4712-8b24-d64ce9919df7/artifacts/06vy2r3w_Capture%209.PNG"
    ],
    workstations: 200
  }
};

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [study, setStudy] = useState(DEFAULT_CASE_STUDIES[slug] || null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useMetaDescription(study ? `${study.title} — corporate office furniture case study by Ezeefit. ${study.scope || ''}` : null);

  useEffect(() => {
    const fetchCaseStudy = async () => {
      try {
        const response = await axios.get(`${API}/case-studies/${slug}`);
        if (response.data && response.data.title) {
          setStudy(response.data);
        }
        // If API fails or returns empty, we already have DEFAULT_CASE_STUDIES[slug] loaded
        
        // Fetch related blogs
        try {
          const blogsResponse = await axios.get(`${API}/blogs`);
          const related = blogsResponse.data?.slice(0, 3) || [];
          setRelatedBlogs(related);
        } catch (e) {
          // Silently fail for blogs
        }
      } catch (err) {
        console.error("Failed to fetch case study, using default:", err);
        // Keep using default which is already set
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudy();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-[#F97316] border-t-transparent rounded-full mx-auto" />
          <p className="text-[#64748B] mt-4">Loading case study...</p>
        </div>
      </div>
    );
  }

  if (!study) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Case Study Not Found</h2>
          <Button asChild>
            <Link to="/case-studies">Back to Case Studies</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="case-study-detail-page">
      <Helmet>
        <meta name="description" content={`${study.title} — corporate office furniture case study by Ezeefit. ${study.scope || ''}`} />
        <link rel="canonical" href={`https://ezeefitofficesystems.in/case-studies/${slug}`} />
        <meta property="og:title" content={`${study.title} | Ezeefit Case Study`} />
        <meta property="og:url" content={`https://ezeefitofficesystems.in/case-studies/${slug}`} />
      </Helmet>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ezeefitofficesystems.in/" },
              { "@type": "ListItem", "position": 2, "name": "Case Studies", "item": "https://ezeefitofficesystems.in/case-studies" },
              { "@type": "ListItem", "position": 3, "name": study.title, "item": `https://ezeefitofficesystems.in/case-studies/${slug}` }
            ]
          })
        }}
      />
      {/* Hero */}
      <section className="relative py-24 bg-[#0F172A] noise-overlay overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={study.images?.[0]}
            alt={`${study.title} - Corporate office furniture project by Ezeefit Mumbai`}
            className="w-full h-full object-cover" loading="lazy" decoding="async" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Case Studies
            </Link>

            <span className="inline-block bg-[#F97316] text-white text-sm font-mono px-4 py-2 uppercase tracking-wider mb-6">
              {study.industry}
            </span>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {study.title}
            </h1>

            <div className="flex flex-wrap gap-6 text-white">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#F97316]" />
                {study.location}
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#F97316]" />
                {study.workstations} Workstations
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#F97316]" />
                {study.timeline}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Project Scope */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 bg-[#F8FAFC] border border-[#E2E8F0]"
            >
              <h3 className="font-mono text-sm text-[#F97316] uppercase tracking-wider mb-4">
                Project Scope
              </h3>
              <p className="text-[#0F172A] font-medium">{study.scope}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-8 bg-[#F8FAFC] border border-[#E2E8F0]"
            >
              <h3 className="font-mono text-sm text-[#F97316] uppercase tracking-wider mb-4">
                Industry
              </h3>
              <p className="text-[#0F172A] font-medium">{study.industry}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="p-8 bg-[#F8FAFC] border border-[#E2E8F0]"
            >
              <h3 className="font-mono text-sm text-[#F97316] uppercase tracking-wider mb-4">
                Delivery Timeline
              </h3>
              <p className="text-[#0F172A] font-medium">{study.timeline}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenge, Solution, Outcome */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-3 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-[#0F172A] flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-[#F97316]" />
              </div>
              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">The Challenge</h3>
              <p className="text-[#64748B] leading-relaxed">{study.challenge}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-16 h-16 bg-[#0F172A] flex items-center justify-center mb-6">
                <Lightbulb className="w-8 h-8 text-[#F97316]" />
              </div>
              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Our Solution</h3>
              <p className="text-[#64748B] leading-relaxed">{study.solution}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-16 h-16 bg-[#0F172A] flex items-center justify-center mb-6">
                <CheckCircle className="w-8 h-8 text-[#F97316]" />
              </div>
              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">The Outcome</h3>
              <p className="text-[#64748B] leading-relaxed">{study.outcome}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-[#0F172A]">Project Gallery</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {study.images?.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="overflow-hidden"
              >
                <img src={image}
                  alt={`${study.title} - Office furniture installation photo ${index + 1} by Ezeefit Mumbai`}
                  className="w-full h-80 object-cover" loading="lazy" decoding="async" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0F172A] noise-overlay">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-lg text-[#94A3B8] mb-8">
            Let us help you create a workspace that drives productivity and reflects your brand. Explore our{" "}
            <Link to="/process" className="text-[#F97316] underline hover:text-orange-400">execution process</Link>{" "}
            or visit our{" "}
            <Link to="/manufacturing" className="text-[#F97316] underline hover:text-orange-400">manufacturing facility</Link>.
          </p>
          <Button
            asChild
            className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-10 py-6 font-semibold uppercase tracking-wider"
          >
            <Link to="/contact">
              Get a Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>

      <ExploreMore currentPage="caseStudies" />
    </div>
  );
}
